import { useCallback, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import SocialButtons from './AccountAccessSocialButtons'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { connect } from 'react-redux'
import { useAppSelector } from 'hooks/reduxHooks'
import TextButton from 'LuxKit/Button/TextButton'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import AccountAccessLegalText from './AccountAccessLegalText'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Group from 'components/utils/Group'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import LineStarIcon from 'LuxKit/Icons/line/LineStarIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import TrustIndicatorsMapper from './AccountAccessTrustIndicatorsMapper'
import FreePreviewSignupHeader from './FreePreviewSignupHeader'
import LineHeartIcon from 'LuxKit/Icons/line/LineHeartIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineUniversityIcon from 'LuxKit/Icons/line/LineUniversityIcon'
import noop from 'lib/function/noop'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { rem } from 'polished'
import styled from 'styled-components'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { trackClientEvent } from 'analytics/analytics'
import AccountAccessOfferPreview from './AccountAccessOfferPreview'
import { isCheckout } from 'selectors/checkoutSelectors'
import AccountAccessUserInput from './AccountAccessUserInput'
import useAccountAccessLoginStart from './useAccountAccessLoginStart'
import LoginCaptcha from './LoginCaptcha'
import useLuxPlusFreePreviewUtils from 'luxPlus/hooks/useLuxPlusFreePreviewUtils'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { AccountAccessModalResult } from './AccountAccessModal.types'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import AccountAccessTourCatalogueHeader from 'components/Account/AccountAccess/AccountAccessTourCatalogueHeader'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import useQueryParams from 'hooks/useQueryParams'
import useLoyaltyConfig from 'loyalty/hooks/useLoyaltyConfig'
import { getLastUsedLoginMethod } from 'lib/auth/lastUsedLoginMethod'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

const Or = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};

  &::after,
  &::before {
    content: '';
    flex: 1;
    border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    display: block;
  }
`

const GuestCheckoutSection = styled.div`
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  padding-top: ${rem(24)};
`

type LoginUIContent = Array<{
  Icon: React.ReactElement
  text: string
}>

type LoginUIData = {
  title: string
  content: LoginUIContent
}

interface GetLoginUIData {
  intl: IntlShape
  isBookingFlow: boolean
  mode?: App.UiAccountModalMode
  loyaltyEnabled: boolean
  enabledBenefits: App.LoyaltyRegionEnabledBenefits | undefined
  isMerchandise: boolean
  loyaltyProgramDisplayName: string
  bookingAuthorityOwnerName?: string
}

const getLoginUIData = ({
  intl,
  isBookingFlow,
  mode,
  loyaltyEnabled,
  enabledBenefits,
  isMerchandise,
  loyaltyProgramDisplayName,
  bookingAuthorityOwnerName,
}: GetLoginUIData): LoginUIData => {
  if (mode === 'wishlistLogin') {
    return {
      title: intl.formatMessage({
        id: '81MaBWDjiLr7KbNDWUxJ',
        defaultMessage: 'Log in or sign up to save your favourite offers',
        description: 'Title for the wishlist login modal shown when a logged-out user clicks the wishlist nav button',
      }),
      content: [
        {
          Icon: <LineHeartIcon />,
          text: intl.formatMessage({
            id: 'cZFQsZzxtp4ysHrXxvv1',
            defaultMessage: 'Create wishlists to organise your dream trips',
            description: 'Benefit text for creating wishlists in the wishlist login modal',
          }),
        },
        {
          Icon: <LineCalendarIcon />,
          text: intl.formatMessage({
            id: 'z0QCotovz6Bw4TukudxF',
            defaultMessage: 'Plan your perfect itinerary with AI',
            description: 'Benefit text for AI trip planning in the wishlist login modal',
          }),
        },
        {
          Icon: <LineUniversityIcon />,
          text: intl.formatMessage({
            id: 'zFjToKS5qEQ8DTbjmHLd',
            defaultMessage: 'Explore must-see attractions',
            description: 'Benefit text for exploring attractions in the wishlist login modal',
          }),
        },
      ],
    }
  } else if (mode === 'tripPlannerLogin') {
    return {
      title: intl.formatMessage({
        id: 'c1hqruFJKvpSB-77Qqas',
        defaultMessage: 'Sign up to start planning your next escape',
        description: 'Title for trip planner login modal in the trip planner feature',
      }),
      content: [
        {
          Icon: <LineHeartIcon />,
          text: intl.formatMessage({
            id: 'gB1Lxnf51n92WaT3J-CT',
            defaultMessage: 'Save your favourite offers',
            description: 'Benefit text for saving favourite offers on the offer page',
          }),
        },
        {
          Icon: <LineCalendarIcon />,
          text: intl.formatMessage({
            id: 'Y_W4IEGYjZHrTiDgzaxZ',
            defaultMessage: 'Easily plan your perfect trip',
            description: 'Benefit text for planning trips in the trip planner feature',
          }),
        },
        {
          Icon: <LineUniversityIcon />,
          text: intl.formatMessage({
            id: 'z6ht1GYxd_qSe6HtdQCy',
            defaultMessage: 'Explore must-see attractions',
            description: 'Benefit text for exploring attractions in the loyalty benefits section',
          }),
        },
      ],
    }
  } else if (mode === 'tripPlannerPagesLogin') {
    return {
      title: intl.formatMessage({
        id: '__b1vVj9EAoorv3QQh_K',
        defaultMessage: 'You’re one step away from your dream itinerary',
        description: 'Title for trip planner pages login modal in the trip planner',
      }),
      content: [
        {
          Icon: <LineHeartIcon />,
          text: intl.formatMessage({
            id: 'gB1Lxnf51n92WaT3J-CT',
            defaultMessage: 'Save your favourite offers',
            description: 'Benefit text for saving favourite offers on the offer page',
          }),
        },
        {
          Icon: <LineCalendarIcon />,
          text: intl.formatMessage({
            id: 'Y_W4IEGYjZHrTiDgzaxZ',
            defaultMessage: 'Easily plan your perfect trip',
            description: 'Benefit text for planning trips in the trip planner feature',
          }),
        },
        {
          Icon: <LineUniversityIcon />,
          text: intl.formatMessage({
            id: 'z6ht1GYxd_qSe6HtdQCy',
            defaultMessage: 'Explore must-see attractions',
            description: 'Benefit text for exploring attractions in the loyalty benefits section',
          }),
        },
      ],
    }
  } else if (mode === 'requestAgentCallbackLogin') {
    return {
      title: intl.formatMessage({
        id: 'Xdfl9j4YR-HqZlsZsdSL',
        defaultMessage: 'Log in to request a callback',
        description: 'Title for request agent callback login modal in the agent portal',
      }),
      content: [
        {
          Icon: <LineGiftIcon />,
          text: intl.formatMessage({
            id: 'lHcEQjNoZXd9yl02yfxC',
            defaultMessage: 'Unlock thousands of exclusive offers',
            description: 'Benefit text for unlocking exclusive offers on the offer page',
          }),
        },
        {
          Icon: <LineStarIcon />,
          text: intl.formatMessage({
            id: 'xMUlykR0QTZx8ckjTyM8',
            defaultMessage: 'Over a million trips taken every year',
            description: 'Benefit text about trips taken in the trip planner feature',
          }),
        },
        {
          Icon: <LinePhoneIcon />,
          text: intl.formatMessage({
            id: '4Z97Vg_eYdCHgliitUDp',
            defaultMessage: 'Our local team are available 24 hours, 7 days',
            description: 'Benefit text about local team availability in the benefits list',
          }),
        },
      ],
    }
  } else if (mode === 'supportLogin') {
    return {
      title: intl.formatMessage({
        id: '1HYQMRzetkoAW__8_5v8',
        defaultMessage: 'Log in or sign up so we can help',
        description: 'Modal title prompting the user to log in or sign up before accessing support chat',
      }),
      content: [],
    }
  } else if (mode === 'employeeOfferLogin') {
    return {
      title: intl.formatMessage({
        id: '376A5RdbAVXnZeLBSLmG',
        defaultMessage: 'Log in to your Luxury Escapes account to unlock your employee benefit',
        description: 'Modal title prompting a Luxury Escapes employee to log in before viewing the staff-only credit card offer',
      }),
      content: [],
    }
  } else if (mode === 'leLiveLogin') {
    return {
      title: intl.formatMessage({
        id: 'qOq00WjiFRE1f4SHacWB',
        defaultMessage: 'Login to claim your free tickets',
        description: 'Heading title for LE Live event registration login modal',
      }),
      content: [
        {
          Icon: <LineGiftIcon />,
          text: intl.formatMessage({
            id: 'tM2BGuNawrIvGhuGTy_z',
            defaultMessage: 'Thousands in prizes & giveaways',
            description: 'Prizes benefit text for LE Live login modal in the account access section',
          }),
        },
        {
          Icon: <LineStarIcon />,
          text: intl.formatMessage({
            id: 'BUlUqhAao4Tb2xJauUL4',
            defaultMessage: 'Exclusive event-only offers',
            description: 'Exclusive offers benefit text for LE Live login modal',
          }),
        },
        {
          Icon: <LineCalendarIcon />,
          text: intl.formatMessage({
            id: 'oGARSf1JsZbAUF_ajqQC',
            defaultMessage: '50+ local & international exhibitors, plus travel insiders and experts',
            description: 'Exhibitors benefit text for LE Live login modal in the account access section',
          }),
        },
      ],
    }
  }
  else if (mode === 'loyaltyLogin') {
    const content = [
      {
        Icon: <LineBedIcon />,
        text: intl.formatMessage({
          id: 'lBBmLSBUSxxlWLeOfGBa',
          defaultMessage: 'Upgrade your stay',
          description: 'Benefit text for upgrading stay in the loyalty benefits section',
        }),
      },
      {
        Icon: <LineTaxiIcon />,
        text: intl.formatMessage({
          id: 'kLMU7eG_SLb2kcw_HEBW',
          defaultMessage: 'Start your escape with chauffeur transfers',
          description: 'Benefit text for chauffeur transfers in the airport transfer booking section',
        }),
      },
    ]

    if (enabledBenefits?.lounge_pass) {
      content.push({
        Icon: <LineChairWithAirplaneIcon />,
        text: intl.formatMessage({
          id: 'qazfNik9HBAIPmhcdA-W',
          defaultMessage: 'Lounge in comfort before your flight',
          description: 'Benefit description text for the airport lounge pass inclusion in the offer',
        }),
      })
    }

    return {
      title: intl.formatMessage({
        id: 'sqCxAC7M_4c6lvaRi2gW',
        defaultMessage: 'Join {programName} and elevate your escapes',
        description: 'Modal title encouraging the user to join the loyalty program and elevate their escapes',
      }, {
        programName: loyaltyProgramDisplayName,
      }),
      content,
    }
  }
  else if (mode === 'loyaltyTierUpgradeCampaignLogin') {
    return {
      title: intl.formatMessage({
        id: 'Sd7Yo7woNs-giIDlHPQC',
        defaultMessage: 'Log in to activate your {programName} Status',
        description: 'Title for the loyalty tier upgrade activation login modal',
      }, {
        programName: loyaltyProgramDisplayName,
      }),
      content: [],
    }
  }
  else if (mode === 'statusMatchLogin') {
    return {
      title: intl.formatMessage({
        id: 'zyrx6TC1hfWN2ho9rw2l',
        defaultMessage: 'Log in to apply for Status Match',
        description: 'Title for the status match application login modal',
      }),
      content: [],
    }
  }
  else if (mode === 'sharedCartInvite') {
    return {
      title: intl.formatMessage({
        id: 'jltxOpAANZbLrEvAPpoQ',
        defaultMessage: 'You have been invited to join a shared cart',
        description: 'Title for shared cart invite login modal in the cart view',
      }),
      content: [],
    }
  }
  else if (mode === 'bookingAuthorityInvite') {
    if (bookingAuthorityOwnerName) {
      return {
        title: intl.formatMessage({
          defaultMessage: '{ownerName} has invited you to their trip.',
          id: 'Un4nQXL59DiUjrbAfCgM',
          description: 'Title for booking authority invite login modal when invite owner name is known',
        }, { ownerName: bookingAuthorityOwnerName }),
        content: [],
      }
    }

    return {
      title: intl.formatMessage({
        defaultMessage: 'You’ve been invited to a booking',
        id: 'qJ9upjz5zQZmiGmShiBC',
        description: 'Fallback title for booking authority invite login modal when invite owner name is not provided',
      }),
      content: [],
    }
  }
  else if (isMerchandise) {
    return {
      title: intl.formatMessage({
        id: 'IMFLlZnP2xwYh17ydj3Y',
        defaultMessage: 'Log in or sign up to view this product',
        description: 'Title for merchandise login modal in the merchandise shop section',
      }),
      content: [],
    }
  }
  else {
    return {
      title: isBookingFlow ? intl.formatMessage({
        id: 'HvqML6ar9RckPoJ8ChLB',
        defaultMessage: 'Log in or sign up to book this exclusive member offer',
        description: 'Title for booking flow login modal in the booking or order summary view',
      }) : intl.formatMessage({
        id: 'vMrSRp5LND0SRQNWJJIV',
        defaultMessage: 'Exclusive prices and more with every booking',
        description: 'Default title for the login modal promoting exclusive prices and member benefits',
      }),
      content: [
        {
          Icon: <LineGiftIcon />,
          text: intl.formatMessage({
            id: 'lHcEQjNoZXd9yl02yfxC',
            defaultMessage: 'Unlock thousands of exclusive offers',
            description: 'Benefit text for unlocking exclusive offers on the offer page',
          }),
        },
        {
          Icon: <LineStarIcon />,
          text: loyaltyEnabled ? intl.formatMessage({
            id: 'p632CLlbjOWXnIFEb04M',
            defaultMessage: 'Earn & redeem points with every booking',
            description: 'Benefit text for earning and redeeming points in the account access section',
          }) : intl.formatMessage({
            id: 'xMUlykR0QTZx8ckjTyM8',
            defaultMessage: 'Over a million trips taken every year',
            description: 'Benefit text about trips taken in the trip planner feature',
          }),
        },
        {
          Icon: <LinePhoneIcon />,
          text: intl.formatMessage({
            id: '4Z97Vg_eYdCHgliitUDp',
            defaultMessage: 'Our local team are available 24 hours, 7 days',
            description: 'Benefit text about local team availability in the benefits list',
          }),
        },
      ],
    }
  }
}

interface Props {
  error?: any
  isStoreMode: boolean
  defaultEmail?: string
  mode?: App.UiAccountModalMode
  onModeChange?: (mode: App.UiAccountModalMode, userId?: string) => void
  hotelOfferDetails?: App.AccountModalOfferDetails
  offerDetails?: App.AccountModalOfferDetails
  dismissable?: boolean
  applePrivateEmailData: App.AppleLoginData | null
  setApplePrivateEmailData: (loginData: App.AppleLoginData | null) => void
  /** Metasearch guest checkout: offers a "Continue without signing in" action that resolves the modal without logging in. */
  guestCheckoutEnabled?: boolean
  /** Reminder link: submit the prefilled email and open the next step. */
  autoContinueEmail?: boolean
  onAutoContinue?: () => void
}

function AccountAccessLoginStart(props: Props) {
  // TripPlanner chat mirrors key login/sign-up steps in ChatEmbeddedAccountAccess.
  // If this flow changes, check with the TripPlanner team whether the embedded chat flow also needs updating.
  const {
    error,
    isStoreMode,
    defaultEmail,
    mode,
    onModeChange = noop,
    hotelOfferDetails,
    offerDetails,
    dismissable,
    applePrivateEmailData,
    setApplePrivateEmailData,
    guestCheckoutEnabled,
    autoContinueEmail,
    onAutoContinue = noop,
  } = props

  const formRef = useRef<HTMLFormElement>(null)
  const isBooking = useAppSelector(isCheckout)
  const { shouldShowFreePreviewSignupPromo: autoEnrollFreePreview } = useLuxPlusFreePreviewUtils()
  const loggedIn = useAppSelector(selectLoggedIn)
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const query = useQueryParams()
  const lastUsedLoginMethod = getLastUsedLoginMethod()
  const intl = useIntl()
  const loyaltyProgramDisplayName = useAppSelector(getLoyaltyProgramDisplayName)
  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  const onContinueAsGuest = useCallback(() => {
    trackClientEvent({
      subject: 'guest_checkout_continue',
      action: 'interaction',
      category: 'logging',
      type: 'operational',
    })
    modalContext?.resolve({ loggedIn: false, continueAsGuest: true })
  }, [modalContext])

  const isSharedCartInvite = mode === 'sharedCartInvite'
  const isBookingAuthorityInvite = mode === 'bookingAuthorityInvite'
  const isTourCatalogue = mode === 'toursCatalogueLogin'
  const isPhoneLoginMethod = query.get('login_method') === 'phone'
  const loginMethod = isPhoneLoginMethod ? 'phone' : undefined
  const { isCheckingEmail, rejectionMessage, reRef, onCheckEmail, onPrivateAppleEmailLogin } = useAccountAccessLoginStart({
    onContinue: onModeChange,
    applePrivateEmailData,
    setApplePrivateEmailData,
    defaultEmail,
    autoContinueEmail,
    onAutoContinue,
    loginMethod,
  })
  const socialMediaLoginsEnabled = config.FACEBOOK_APP_ID || config.GOOGLE_APP_ID || config.APPLE_APP_ID
  // `hide_sso` is used for some deep links that want to suppress SSO buttons, however
  // booking authority invite should always allow other login methods where configured.
  const skipSocialQuery = isPhoneLoginMethod || (query.has('hide_sso') && !isBookingAuthorityInvite)
  const showSocialMediaButtons = !isStoreMode && socialMediaLoginsEnabled && !skipSocialQuery

  const isBookingFlow = isBooking || !!hotelOfferDetails || !!offerDetails

  const [loyaltyConfig] = useLoyaltyConfig()

  const bookingAuthorityOwnerName = query.get('ownerName') ?? undefined

  const { title, content } = useMemo(() => getLoginUIData({
    intl,
    isBookingFlow,
    mode,
    loyaltyEnabled,
    enabledBenefits: loyaltyConfig?.regionEnabledBenefits,
    isMerchandise: !!offerDetails?.merchandiseVariant,
    loyaltyProgramDisplayName,
    bookingAuthorityOwnerName,
  }), [intl, isBookingFlow, mode, loyaltyEnabled, offerDetails?.merchandiseVariant, loyaltyConfig?.regionEnabledBenefits, loyaltyProgramDisplayName, bookingAuthorityOwnerName])

  const isLoyaltyTierUpgradeCampaign = mode === 'loyaltyTierUpgradeCampaignLogin'
  const isStatusMatch = mode === 'statusMatchLogin'
  const isEmployeeOffer = mode === 'employeeOfferLogin'
  const showTrustIndicators = config.LOGIN_TRUST_INDICATORS && !isBookingFlow && !isTourCatalogue && !isBookingAuthorityInvite && !isLoyaltyTierUpgradeCampaign && !isStatusMatch && !isEmployeeOffer

  return (<>
    {/* The modal's own indicator follows auth.processing, which ends as the email check starts */}
    <LoadingIndicator visible={isCheckingEmail} floating />
    <ModalHeader title={title} dismissible={dismissable} isOverlay={isTourCatalogue} />
    <ModalBody>
      {isTourCatalogue && <AccountAccessTourCatalogueHeader />}
      <ModalContent flushVerticalTop={!!isBookingAuthorityInvite}>
        <VerticalSpacer gap={24}>
          <VerticalSpacer ref={formRef} gap={24} as="form" onSubmit={onCheckEmail} name="loginForm">
            {error?.message && <MessageBanner kind="critical">{error.message}</MessageBanner>}
            {!error?.message && rejectionMessage && <MessageBanner kind="critical">{rejectionMessage}</MessageBanner>}
            {isBookingFlow && <AccountAccessOfferPreview hotelOfferDetails={hotelOfferDetails} offerDetails={offerDetails} />}
            {isSharedCartInvite && <BodyText variant="medium">{intl.formatMessage({
              id: 'ktTP0HMkPIKuxi4YWT86',
              defaultMessage: 'Please log in or create an account to securely access this cart.',
              description: 'Description text for shared cart invite in the shopping cart interface',
            })}
            </BodyText>}
            {!isBookingFlow && !isTourCatalogue && !isSharedCartInvite && !isBookingAuthorityInvite && !isLoyaltyTierUpgradeCampaign && content.length > 0 && (
              <Group direction="vertical" gap={20}>
                <Group direction="vertical" gap={4}>
                  {content.map(({ Icon, text }) => <BodyTextBlock
                    key={text}
                    variant="medium"
                    startIcon={Icon}
                  >
                    {text}
                  </BodyTextBlock>)}
                </Group>
              </Group>
            )}
            {autoEnrollFreePreview && <FreePreviewSignupHeader />}
            {isBookingAuthorityInvite && (
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'sd47eJT_7ekCoBpIVto_',
                  defaultMessage: 'Please log in or create an account to securely access this booking.',
                  description: 'Description text for booking authority invite in the account access section',
                })}
              </BodyText>
            )}
            {mode === 'loyaltyTierUpgradeCampaignLogin' && (
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'Pser1VCqwnfmZk2r4xjo',
                  defaultMessage: 'To activate your complimentary {programName} Status, please log in or create a Luxury Escapes account.',
                  description: 'Description text for the loyalty tier upgrade activation login modal',
                }, {
                  programName: loyaltyProgramDisplayName,
                })}
              </BodyText>
            )}
            {mode === 'statusMatchLogin' && (
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'u4A7qpp2D3VmlMT27WFm',
                  defaultMessage: 'To submit your status match application, please log in or create a Luxury Escapes account.',
                  description: 'Description text for the status match application login modal',
                })}
              </BodyText>
            )}
            {showTrustIndicators && <Group direction="horizontal" horizontalAlign="center">
              <TrustIndicatorsMapper />
            </Group>}
            <Group direction="vertical" gap={32} tabletGap={24}>
              <AccountAccessUserInput
                loginMethod={loginMethod}
                defaultEmail={loginMethod === 'phone' ? undefined : defaultEmail}
                showLastUsedTag={!loginMethod && (lastUsedLoginMethod === 'email' || lastUsedLoginMethod === 'phone' || lastUsedLoginMethod === 'otp')}
              />
              <LoginCaptcha turnstileAction="account_lookup" ref={reRef} />
              <TextButton
                kind="primary"
                size="large"
                type="submit"
                disabled={isCheckingEmail}
              >
                {intl.formatMessage({
                  id: 'l-OntyzIV9NVn1GxzNSI',
                  defaultMessage: 'Continue',
                  description: 'Button text to continue to the next step in the multi-step booking flow',
                })}
              </TextButton>
            </Group>
          </VerticalSpacer>
          {showSocialMediaButtons && <>
            <Or>
              <BodyText colour="light-foreground-alt" variant="medium">{intl.formatMessage({
                id: 'pvuuq7EvzXo38JF3v2OL',
                defaultMessage: 'Other ways to log in',
                description: 'Text for social login section divider on the login or signup page',
              })}
              </BodyText>
            </Or>
            <SocialButtons
              mode="login"
              inline
              onPrivateAppleEmailLogin={onPrivateAppleEmailLogin}
              lastUsedLoginMethod={lastUsedLoginMethod}
            />
          </>}
          <AccountAccessLegalText />
          {/*
            Sits below the legal text as its own block so the sign-in consents above
            it — social providers, Societe enrolment, marketing — read as belonging to
            the sign-in options rather than to this button, which carries its own.
          */}
          {guestCheckoutEnabled && <GuestCheckoutSection>
            <VerticalSpacer gap={12}>
              <TextButton
                kind="secondary"
                size="large"
                fit="full-width"
                data-testid="continue-as-guest-button"
                onClick={onContinueAsGuest}
              >
                {intl.formatMessage({
                  id: 'GO3r1j1m5MwmZMLDD89F',
                  defaultMessage: 'Continue without signing in',
                  description: 'Button in the login modal that lets metasearch customers go straight to checkout without a sign-in step',
                })}
              </TextButton>
              <AccountAccessLegalText modalType="guestCheckout" />
            </VerticalSpacer>
          </GuestCheckoutSection>}
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </>)
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    isStoreMode: state.system.storeMode,
  }
}

export default connect(mapStateToProps)(AccountAccessLoginStart)
